import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import domReady from '@wordpress/dom-ready';
import { Destination } from './components/Destination';
import { DestinationImage } from './components/DestinationImage';
import { DestinationLinks } from './components/DestinationLinks';
import { DestinationsNav } from './components/DestinationsNav';

// Frame's own aspect ratio (matches &__image-track / &__image-wrapper in
// destinations.scss) -- needed to work out how tall a rotated frame's
// bounding box actually is.
const FRAME_ASPECT_RATIO = 601 / 744;

// Matches $ocean-mobile in scss/_thresh.scss. Mobile drops the pinned
// scroll-through entirely, per the mobile design (node 4378:3280): the intro
// slide alone, no nav, no crossfade -- just a normal-flow band with a CTA out
// to the Destinations page. Kept in sync by hand; the two have to agree or
// the JS would size a section the CSS has already un-pinned.
const MOBILE_QUERY = '(max-width: 782px)';

const useIsMobile = () =>
	{
	const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

	useEffect(() =>
		{
		const query = window.matchMedia(MOBILE_QUERY);
		const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
		query.addEventListener('change', onChange);
		// Re-read on mount in case the viewport changed between the initial
		// state and the effect firing.
		setIsMobile(query.matches);
		return () => query.removeEventListener('change', onChange);
		}, []);

	return isMobile;
	};

interface BlockProps
	{
	container: HTMLElement;
	destinations: Destination[];
	hasIntro: boolean;
	imageRotation: number;
	imageGap: number;
	buttonLabel: string;
	buttonUrl: string;
	}

// Reads scroll position against the section's own bounds to derive a
// fractional "which tab" progress value -- CSS `position: sticky` (see
// &__sticky) does the actual pinning, this just captures where the user
// has scrolled to within that pinned span. No scroll-hijacking/preventDefault.
const useScrollProgress = (container: HTMLElement, count: number) =>
	{
	const [rawIndex, setRawIndex] = useState(0);

	useEffect(() =>
		{
		if (count <= 1)
			{
			return;
			}

		const setSectionHeight = () =>
			{
			container.style.height = `${count * window.innerHeight}px`;
			};

		let ticking = false;
		const onScroll = () =>
			{
			if (ticking)
				{
				return;
				}
			ticking = true;
			requestAnimationFrame(() =>
				{
				const vh = window.innerHeight;
				const sectionTop = container.getBoundingClientRect().top + window.scrollY;
				const pinnedDistance = (count - 1) * vh;
				const scrolledIntoPin = Math.min(Math.max(window.scrollY - sectionTop, 0), pinnedDistance);
				setRawIndex(pinnedDistance > 0 ? scrolledIntoPin / vh : 0);
				ticking = false;
				});
			};

		setSectionHeight();
		onScroll();

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', setSectionHeight);

		return () =>
			{
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', setSectionHeight);
			container.style.height = '';
			};
		}, [container, count]);

	return rawIndex;
	};

// A rotated box's own bounding-box height is taller than its unrotated
// height -- a flat 100% translateY step isn't enough clearance between
// stacked frames once they're tilted, hence the overlap. This is the
// multiplier (as a percentage) that just touches, before the user's own
// gap is added on top.
const getFrameStepPercent = (rotationDegrees: number, gapPercent: number) =>
	{
	const rotationRad = (Math.abs(rotationDegrees) * Math.PI) / 180;
	const boundingHeightRatio = Math.cos(rotationRad) + FRAME_ASPECT_RATIO * Math.sin(rotationRad);
	return boundingHeightRatio * 100 + gapPercent;
	};

const BlockRuntime = ({ container, destinations, hasIntro, imageRotation, imageGap, buttonLabel, buttonUrl }: BlockProps) =>
	{
	const isMobile = useIsMobile();
	// Passing 1 is what stands the scroll machinery down: useScrollProgress
	// bails on count <= 1, so it never sets the multi-viewport section height
	// (and its cleanup clears any height a previous desktop render left).
	const visible = isMobile ? destinations.slice(0, 1) : destinations;
	const rawIndex = useScrollProgress(container, visible.length);
	const activeIndex = Math.round(rawIndex);
	const stepPercent = getFrameStepPercent(imageRotation, imageGap);

	return (
		<>
		<div className="wp-block-ocean-destinations__sticky">
			<div className="wp-block-ocean-destinations__layout wp-block-ocean-destinations__layout--frontend">
				<div className="wp-block-ocean-destinations__content">
					{visible.map((destination, index) =>
						{
						const opacity = Math.max(0, 1 - Math.abs(index - rawIndex) * 2);
						if (opacity <= 0)
							{
							return null;
							}
						return (
							<div
								key={index}
								className="wp-block-ocean-destinations__text-panel"
								style={{ opacity }}
								aria-hidden={index !== activeIndex}
								>
								<h2 className="wp-block-ocean-destinations__heading">{destination.heading}</h2>
								<div
									className="wp-block-ocean-destinations__description"
									dangerouslySetInnerHTML={{ __html: destination.description }}
									/>
								<DestinationLinks
									exploreLabel={destination.exploreLabel}
									exploreUrl={destination.exploreUrl}
									discoverLabel={destination.discoverLabel}
									discoverUrl={destination.discoverUrl}
									ctaLabel={destination.ctaLabel}
									ctaUrl={destination.ctaUrl}
									/>
								{buttonLabel && (
									<a className="wp-block-ocean-destinations__button" href={buttonUrl || '#'}>
										{buttonLabel}
									</a>
									)}
							</div>
							);
						})}
				</div>
				<div className="wp-block-ocean-destinations__image-track">
					{visible.map((destination, index) => (
						<DestinationImage
							key={index}
							mediaUrl={destination.mediaUrl}
							mediaAlt={destination.mediaAlt}
							index={index}
							rotationDegrees={imageRotation}
							translateY={`${(index - rawIndex) * stepPercent}%`}
							stacked
							/>
						))}
				</div>
			</div>
			{!isMobile && destinations.length > 1 && (
				<div className="wp-block-ocean-destinations__nav-wrapper">
					<DestinationsNav destinations={destinations} activeIndex={activeIndex} hasIntro={hasIntro} />
				</div>
				)}
		</div>
		</>
		);
	};

domReady(() =>
	{
	const containers = document.querySelectorAll('.wp-block-ocean-destinations');

	containers.forEach((container) =>
		{
		const dataset = (container as HTMLElement).dataset;
		const destinations: Destination[] = dataset.destinations ? JSON.parse(dataset.destinations) : [];
		const hasIntro = dataset.hasIntro === '1';
		const imageRotation = dataset.imageRotation ? parseFloat(dataset.imageRotation) : 11.39;
		const imageGap = dataset.imageGap ? parseFloat(dataset.imageGap) : 5;
		const buttonLabel = dataset.buttonLabel || '';
		const buttonUrl = dataset.buttonUrl || '';

		const root = createRoot(container);
		root.render(
			<BlockRuntime
				container={container as HTMLElement}
				destinations={destinations}
				hasIntro={hasIntro}
				imageRotation={imageRotation}
				imageGap={imageGap}
				buttonLabel={buttonLabel}
				buttonUrl={buttonUrl}
				/>
			);
		});
	});
